import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { fmtSydneyDateTime } from "@/lib/timezone";

const STATUS_META = {
  pending: { color: "#C9A85F", Icon: Clock, label: "Pending" },
  approved: { color: "#059669", Icon: CheckCircle2, label: "Approved" },
  declined: { color: "#a1a1aa", Icon: XCircle, label: "Declined" },
};

// Every consumer booking time renders in Sydney tz.
const fmt = fmtSydneyDateTime;

export default function MyRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/requests/consumer/me");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial fetch on mount

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8">
      <div className="border border-border">
        <div className="border-b border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → your requests
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight">
            Booking history · {user?.name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Every slot you've requested — pending, approved, or declined.
          </p>
        </div>

        {rows == null && (
          <div className="p-6 space-y-3" data-testid="my-requests-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton-shimmer h-24 rounded-sm" />
            ))}
          </div>
        )}
        {rows != null && rows.length === 0 && (
          <div className="p-8">
            <p className="mono text-xs text-muted-foreground">
              No requests yet.
            </p>
            <Link
              to="/"
              data-testid="my-requests-browse-link"
              className="mt-3 inline-block underline underline-offset-4 text-sm"
            >
              Browse openings →
            </Link>
          </div>
        )}
        {rows != null && rows.length > 0 && (
          <ul>
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <li
                  key={r.id}
                  data-testid={`my-request-row-${r.id}`}
                  className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto_auto] md:items-center md:gap-6 md:px-6"
                >
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {fmt(r.created_at)}
                    </p>
                    <p className="mt-1 text-base leading-tight">
                      Request · {r.id.slice(0, 8)}
                    </p>
                    {r.note && (
                      <p className="mt-1 text-xs text-muted-foreground italic">
                        “{r.note}”
                      </p>
                    )}
                  </div>
                  <span
                    className="mono inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] uppercase tracking-widest"
                    style={{ color: meta.color, borderColor: meta.color }}
                  >
                    <Icon size={11} /> {meta.label}
                  </span>
                  <Link
                    to={`/status/${r.id}`}
                    data-testid={`my-request-open-${r.id}`}
                    className="border border-border px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-foreground transition-colors"
                  >
                    Open
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
